import type { IWorkflowResponse } from '@escloud/types';
import { Database } from 'lucide-react';
import { useEffect, useRef } from 'react';

import type { SlotDescriptor } from './slot-card';

import type { WizardState } from './index';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDataset } from '@/hooks/use-datasets';
import { formatDicomDate, formatDicomName } from '@/lib/dicom';

type PatientDetails = WizardState['patientDetails'];

interface PatientDetailsStepProps {
    value: PatientDetails;
    onChange: (next: PatientDetails) => void;
    /**
     * First filled slot's InputDataset id. When provided, the step
     * fetches the full record and pre-fills empty form fields from its
     * DICOM Q/R data. Subsequent picks DO NOT re-trigger pre-fill —
     * the first dataset is canonical.
     */
    firstPickedDatasetId: string | null;
    /** Workflow definition — used for the dataset summary header and
     *  to build the auto-filled case name. */
    workflow: IWorkflowResponse;
    /** Every VOLUME slot in workflow order; drives the summary rows. */
    slots: SlotDescriptor[];
    /** Slot key → bound InputDataset id (catalogue pick or eager upload). */
    slotPicks: Record<string, string>;
}

/**
 * Patient details step.
 *
 * The form is a controlled view over `wizardState.patientDetails` —
 * RHF is intentionally absent here because the wizard owns the form
 * state and we avoid an extra sync layer. Inline `required` validation
 * is the only constraint; deeper checks happen at the API boundary.
 *
 * DICOM pre-fill: when the first slot was filled from the catalogue,
 * we query the full InputDataset and copy patientName / DoB / sex into
 * empty fields. User-edited fields are never overwritten — once a
 * field has any value, the pre-fill skips it.
 */
export function PatientDetailsStep({
    value,
    onChange,
    firstPickedDatasetId,
    workflow,
    slots,
    slotPicks,
}: PatientDetailsStepProps) {
    const { data: pickedDataset } = useDataset(firstPickedDatasetId ?? undefined);

    // Pre-fill must run AT MOST ONCE per dataset id — if the user
    // clears a field after pre-fill, we don't refill it. A ref tracks
    // which id we've already pre-filled.
    const prefilledForRef = useRef<string | null>(null);
    useEffect(() => {
        if (!pickedDataset) return;
        if (prefilledForRef.current === pickedDataset.id) return;
        prefilledForRef.current = pickedDataset.id;

        const prefill = extractPatientPrefill(
            {
                patientName: pickedDataset.patientName,
                patientBirthDate: pickedDataset.patientBirthDate,
                patientSex: pickedDataset.patientSex,
            },
            workflow.name
        );
        // Apply only to currently-empty fields so we never clobber
        // user input that may have happened between the pick and the
        // dataset query resolving.
        let dirty = false;
        const next: PatientDetails = { ...value };
        for (const [k, v] of Object.entries(prefill) as [keyof PatientDetails, string | undefined][]) {
            if (!v) continue;
            if (next[k] && next[k].trim().length > 0) continue;
            next[k] = v;
            dirty = true;
        }
        if (dirty) onChange(next);
        // `value` and `onChange` are read via closure on purpose; the
        // ref above ensures pre-fill runs at most once per dataset id
        // so we don't want this effect re-running on every keystroke.
    }, [pickedDataset, workflow.name]);

    const update = (patch: Partial<PatientDetails>) => {
        onChange({ ...value, ...patch });
    };

    return (
        <div className="mx-auto max-w-2xl">
            <div className="space-y-5 rounded-md border bg-card p-6">
                <FormField
                    id="case-name"
                    label="Case name"
                    required
                    error={value.name.trim().length === 0 ? 'Case name is required' : null}
                >
                    <Input
                        id="case-name"
                        value={value.name}
                        onChange={(e) => update({ name: e.target.value })}
                        placeholder="e.g. EVAR planning · Johnson"
                        maxLength={255}
                    />
                </FormField>

                <div className="grid gap-4 sm:grid-cols-2">
                    <FormField id="patient-lastname" label="Last name">
                        <Input
                            id="patient-lastname"
                            value={value.lastname}
                            onChange={(e) => update({ lastname: e.target.value })}
                            maxLength={100}
                        />
                    </FormField>
                    <FormField id="patient-firstname" label="First name">
                        <Input
                            id="patient-firstname"
                            value={value.firstname}
                            onChange={(e) => update({ firstname: e.target.value })}
                            maxLength={100}
                        />
                    </FormField>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <FormField id="patient-dob" label="Date of birth">
                        <Input
                            id="patient-dob"
                            type="date"
                            value={value.dob}
                            onChange={(e) => update({ dob: e.target.value })}
                        />
                    </FormField>
                    <FormField id="patient-gender" label="Sex">
                        <Select
                            value={value.gender || undefined}
                            onValueChange={(v) => update({ gender: v })}
                        >
                            <SelectTrigger id="patient-gender">
                                <SelectValue placeholder="Select sex" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="female">Female</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                    </FormField>
                </div>

                {firstPickedDatasetId && (
                    <p className="text-xs text-muted-foreground">
                        Empty fields are pre-filled from the picked DICOM dataset when available — your edits
                        always win.
                    </p>
                )}
            </div>

            <section className="mt-4 rounded-md border bg-card p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Imaging
                </h2>
                <ul className="mt-3 divide-y">
                    {slots.map((slot) => (
                        <SlotSummaryRow
                            key={slot.key}
                            slot={slot}
                            pickedDatasetId={slotPicks[slot.key] ?? null}
                        />
                    ))}
                </ul>
            </section>
        </div>
    );
}

/**
 * Read-only summary of one workflow slot. Renders the slot name, its
 * required/optional badge, and either the picked dataset's identity
 * (series + patient + study date) or a "Not filled" placeholder.
 */
function SlotSummaryRow({ slot, pickedDatasetId }: { slot: SlotDescriptor; pickedDatasetId: string | null }) {
    const { data } = useDataset(pickedDatasetId ?? undefined);
    const required = slot.definition.required !== false;

    return (
        <li className="flex items-center gap-3 py-3 text-sm">
            <Database className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <span className="font-medium">{slot.definition.name}</span>
                    {required ? (
                        <Badge variant="secondary" className="text-[10px]">
                            Required
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="text-[10px]">
                            Optional
                        </Badge>
                    )}
                </div>
                {pickedDatasetId ? (
                    data ? (
                        <p className="truncate text-xs text-muted-foreground">
                            {[
                                data.seriesDescription,
                                formatDicomName(data.patientName),
                                formatDicomDate(data.studyDate),
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </p>
                    ) : (
                        <p className="truncate text-xs text-muted-foreground">
                            Dataset …{pickedDatasetId.slice(-12)}
                        </p>
                    )
                ) : (
                    <p className="text-xs italic text-muted-foreground">Not filled</p>
                )}
            </div>
        </li>
    );
}

/** Local field wrapper — simpler than shadcn's RHF-tied Form primitives. */
function FormField({
    id,
    label,
    required,
    error,
    children,
}: {
    id: string;
    label: string;
    required?: boolean;
    error?: string | null;
    children: React.ReactNode;
}) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id} className="text-sm">
                {label}
                {required && <span className="ml-1 text-destructive">*</span>}
            </Label>
            {children}
            {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
    );
}

/**
 * DICOM patient block → wizard patient details.
 *
 * Returned fields are always non-empty strings; absent inputs map to
 * `undefined` so callers can use a simple "only fill empty fields"
 * merge pattern.
 *
 * Patient name follows DICOM PN: `LAST^FIRST^MIDDLE^PREFIX^SUFFIX`,
 * with alternate-character-set groups separated by `=` (alphabetic
 * first). We use the alphabetic group's family + given.
 *
 * Birth date converts `YYYYMMDD` → `YYYY-MM-DD` (HTML date-input shape).
 *
 * Sex codes: `M` / `F` / `O` map to the wizard's `male / female /
 * other` shape; anything else is ignored (unknown / U).
 *
 * Case name: when at least one of `lastname` / `firstname` could be
 * extracted, the helper composes `<workflowName> - <Lastname> <Firstname>`
 * (with the missing half omitted). Callers pass `workflowName` for the
 * prefix; if omitted, the case name is left undefined so the user can
 * type one manually.
 */
export function extractPatientPrefill(
    meta: {
        patientName?: string | null;
        patientBirthDate?: string | null;
        patientSex?: string | null;
    },
    workflowName?: string
): Partial<PatientDetails> {
    const out: Partial<PatientDetails> = {};

    if (meta.patientName) {
        const alphabetic = meta.patientName.split('=')[0] ?? '';
        const [last, first] = alphabetic.split('^');
        if (last && last.trim()) out.lastname = last.trim();
        if (first && first.trim()) out.firstname = first.trim();
    }

    if (meta.patientBirthDate && /^\d{8}$/.test(meta.patientBirthDate)) {
        out.dob = `${meta.patientBirthDate.slice(0, 4)}-${meta.patientBirthDate.slice(4, 6)}-${meta.patientBirthDate.slice(6, 8)}`;
    }

    switch (meta.patientSex) {
        case 'M':
            out.gender = 'male';
            break;
        case 'F':
            out.gender = 'female';
            break;
        case 'O':
            out.gender = 'other';
            break;
    }

    if (workflowName && (out.lastname || out.firstname)) {
        const patient = [out.lastname, out.firstname].filter(Boolean).join(' ');
        out.name = `${workflowName} - ${patient}`;
    }

    return out;
}
